import type { Placement, Schedule, ScheduledRule } from "../interface/schedule"
import { buildPlacementPatch } from "./patches"
import {
  formatMoscowInput,
  parseMoscowInput,
  validateSchedule,
} from "./moscowTime"
import equal from "fast-deep-equal"

export const isChannelId = (value: unknown): value is string =>
  typeof value === "string" && /^(?:0|-?[1-9][0-9]*)$/.test(value)
export const placementsPath = "/telegram/post-bot/placements"
export const placementPath = (id: string) =>
  `${placementsPath}/${encodeURIComponent(id)}`
export const newMoscowRule = (): ScheduledRule => ({
  type: "schedule",
  weekDays: [],
  hour: [],
  minute: [0],
  tz: 3,
})

/** Local draft only: bounds are Moscow field strings, never pass this directly to an API. */
export interface PlacementDraft extends Placement {
  /** Read-only source metadata. Never included in a placement request. */
  originalBounds: Pick<Schedule, "dateStart" | "dateEnd">
}
export function placementDraft(value: Placement): PlacementDraft {
  const schedule = structuredClone(value.schedule)
  for (const field of ["dateStart", "dateEnd"] as const) {
    if (typeof schedule[field] !== "string") continue
    try {
      // Canonical §6 explicitly documents naive legacy schedule reads as Moscow.
      schedule[field] = formatMoscowInput(schedule[field], {
        legacyNaive: true,
      })
    } catch {
      // Unknown legacy shapes stay visible and invalid, never silently cleared.
    }
  }
  return {
    ...value,
    schedule,
    originalBounds: {
      dateStart: value.schedule.dateStart,
      dateEnd: value.schedule.dateEnd,
    },
  }
}
export function scheduleFromDraft(
  value: Schedule,
  baseline?: PlacementDraft,
): Schedule {
  const schedule = structuredClone(value)
  for (const field of ["dateStart", "dateEnd"] as const) {
    const local = schedule[field]
    if (typeof local !== "string") continue
    const original = baseline?.originalBounds[field]
    // Keep even the original textual offset/precision for an untouched bound.
    // In particular an existing instant inside a clock fold needs no new guess.
    schedule[field] =
      local === baseline?.schedule[field] &&
      typeof original === "string" &&
      /(?:Z|[+-]\d{2}:\d{2})$/.test(original)
        ? original
        : parseMoscowInput(local)
  }
  validateSchedule(schedule)
  return schedule
}

/** Comparison only, after validation; wire values retain their original offsets. */
const scheduleInstants = (schedule: Schedule) => ({
  ...schedule,
  dateStart:
    typeof schedule.dateStart === "string"
      ? Date.parse(schedule.dateStart)
      : schedule.dateStart,
  dateEnd:
    typeof schedule.dateEnd === "string"
      ? Date.parse(schedule.dateEnd)
      : schedule.dateEnd,
})
export function placementDraftPatch(
  baseline: PlacementDraft,
  draft: PlacementDraft,
) {
  const patch = buildPlacementPatch(baseline, draft)
  if (patch?.channelId !== undefined && !isChannelId(patch.channelId))
    throw new Error("Выберите канал")
  if (patch?.schedule) {
    patch.schedule = scheduleFromDraft(patch.schedule, baseline)
    let unchanged = false
    try {
      unchanged = equal(
        scheduleInstants(patch.schedule),
        scheduleInstants(scheduleFromDraft(baseline.schedule, baseline)),
      )
    } catch {
      // A corrected schedule may replace an invalid legacy read.
    }
    if (unchanged)
      return buildPlacementPatch(baseline, {
        ...draft,
        schedule: baseline.schedule,
      })
  }
  return patch
}
